import React, {AriaAttributes, useEffect, useState} from 'react';

import {AvatarSize, IconData, useActionHandlers} from '@gravity-ui/uikit';

import {block} from '../../cn';
import {User} from '../models';

import {DefaultAvatar} from './DefaultAvatar';
import {InviteeAvatar} from './InviteeAvatar';
import {isInviteeProps} from './helpers';

import './UserDropdownAvatar.scss';

export type UserData = Partial<
    Pick<User, 'avatarHost' | 'avatarId' | 'avatarData' | 'avatar' | 'login' | 'isFederationUser'>
>;

export interface InviteeDropdownAvatarProps {
    invitee: true;
    className?: string;
    size?: AvatarSize;
    title?: string;
    onClick?: (e: React.MouseEvent) => void;
    ariaAttributes?: AriaAttributes;
}

export interface DefaultDropdownAvatarProps {
    user?: UserData;
    url?: string;
    size?: AvatarSize;
    title?: string;
    className?: string;
    internal?: boolean;
    federation?: boolean;
    currentOrgId?: string;
    onClick?: (e: React.MouseEvent) => void;
    ariaAttributes?: AriaAttributes;
    organizationIcon?: IconData;
    organizationIconSize?: number;
}

const b = block('user-dropdown-avatar');

/**
 * @deprecated Use UserAvatar component
 */
export const UserDropdownAvatar = React.forwardRef<
    HTMLDivElement,
    DefaultDropdownAvatarProps | InviteeDropdownAvatarProps
>((props, ref) => {
    const {size = 'm', className, onClick, ariaAttributes, title} = props;
    const interactive = Boolean(onClick);

    // HACK: Used to re-render avatar image on client with real retina value (necessary for SSR)
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    const invitee = isInviteeProps(props);
    const federation = (props as DefaultDropdownAvatarProps).federation;
    const currentOrgId = (props as DefaultDropdownAvatarProps).currentOrgId;
    const isClickable = Boolean(onClick);

    const {onKeyDown} = useActionHandlers(onClick);

    return (
        <div
            key={Number(mounted)}
            ref={ref}
            className={b(
                {
                    size,
                    federation: federation && !invitee,
                    interactive: interactive,
                    withIcon: Boolean(currentOrgId) || invitee,
                },
                className,
            )}
            role={isClickable ? 'button' : undefined}
            onClick={onClick}
            onKeyDown={isClickable ? onKeyDown : undefined}
            tabIndex={isClickable ? 0 : undefined}
            title={title}
            {...ariaAttributes}
        >
            {invitee ? (
                <InviteeAvatar {...(props as InviteeDropdownAvatarProps)} />
            ) : (
                <DefaultAvatar {...(props as DefaultDropdownAvatarProps)} />
            )}
        </div>
    );
});

UserDropdownAvatar.displayName = 'UserDropdownAvatar';
